import React from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import AddWorkflowModal, {
  TabContentHelpers
} from "@/pages/settings/components/shared/stageStudio/addWorkflow/AddWorkflowModal";
import { WorkflowDetailsFormData } from "@/pages/settings/components/shared/stageStudio/addWorkflow/types";
import WorkflowTypesTab from "./WorkflowTypesTab";
import WorkflowList from "./WorkflowList";
import {
  useCreateQuestionnaireMutation,
  useAllQuestionnaires
} from "@/entities/settings/hooks";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import { DealQuestionnaire, DealStage } from "@/pages/deals/types";
import {
  getQuestionnaireTypeValue,
  getWorkflowTypeOptions,
  getWorkflowTypeValueFromId
} from "./utils";
import { ScoreType } from "@/pages/deals/scoreConfig";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  stageId?: string;
}

const DealAddWorkflowModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  stageId
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const createQuestionnaireMutation = useCreateQuestionnaireMutation();
  const { data: questionnairesData } = useAllQuestionnaires();
  const { data: stagesData } = useDealStages();

  const systemQuestionnaires = questionnairesData?.questionnaires?.filter(
    (questionnaire: DealQuestionnaire) => questionnaire.isSystem === true
  );
  const userQuestionnaires = questionnairesData?.questionnaires?.filter(
    (questionnaire: DealQuestionnaire) => questionnaire.isSystem === false
  );
  const hasUserWorkflows = userQuestionnaires && userQuestionnaires.length > 0;

  const tabOptions: Record<string, string> = {
    "workflow-types": t("workflows.tabs.workflowTypes"),
    "out-of-the-box": t("workflows.tabs.outOfTheBox"),
    ...(hasUserWorkflows && {
      "your-workflows": t("workflows.tabs.yourWorkflows")
    })
  };

  const stageOptions = (stagesData?.stages || []).map((stage: DealStage) => ({
    id: stage.id.toString(),
    displayName: stage.name
  }));

  const handleSubmit = async (data: WorkflowDetailsFormData) => {
    try {
      const response = await createQuestionnaireMutation.mutateAsync({
        data: {
          name: data.workflowName,
          description: data.workflowDescription,
          stageId: parseInt(data.workflowStage),
          type: getQuestionnaireTypeValue(data.workflowType),
          questionnaireOrder: 1,
          kpiValue:
            data.workflowType === QuestionnaireType.SIMPLE
              ? ScoreType.QUALIFICATION
              : ScoreType.GENERAL,
          isGuardrails: false,
          isSuccessCriteriaEnabled: false
        }
      });

      queryClient.invalidateQueries({
        queryKey: ["settings/deals/stages/stageById"]
      });
      setIsOpen(false);

      if (response?.id) {
        navigate(`/settings/deals/stages/${data.workflowStage}/${response.id}`);
      }
    } catch (error) {
      console.error("Error creating questionnaire:", error);
    }
  };

  const renderTabContent = (
    activeTab: string,
    { selectType }: TabContentHelpers
  ) => {
    switch (activeTab) {
      case "out-of-the-box":
        return (
          <WorkflowList
            questionnaires={systemQuestionnaires}
            stageId={stageId}
          />
        );
      case "your-workflows":
        return <WorkflowList questionnaires={userQuestionnaires} />;
      case "workflow-types":
      default:
        return (
          <WorkflowTypesTab
            onStartBuilding={(workflowType) =>
              selectType(getWorkflowTypeValueFromId(workflowType))
            }
          />
        );
    }
  };

  return (
    <AddWorkflowModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      stageId={stageId}
      defaultTab="workflow-types"
      tabOptions={tabOptions}
      renderTabContent={renderTabContent}
      workflowTypeOptions={getWorkflowTypeOptions(t)}
      stageOptions={stageOptions}
      onSubmit={handleSubmit}
      isSubmitting={createQuestionnaireMutation.isPending}
    />
  );
};

export default DealAddWorkflowModal;
